import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Menu,
  MenuItem,
  Divider,
  IconButton,
  Fab,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
  Pagination,
  Tabs,
  Tab,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Filter,
  List,
  LayoutGrid,
  Calendar,
  SortAsc,
  ChevronDown,
  X,
} from 'lucide-react';

import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useAppDispatch, useAppSelector } from '@/store';
import { openModal, selectTaskViewMode, setTaskViewMode } from '@/store/slices/uiSlice';
import { selectTaskFilters, updateTaskFilter, clearTaskFilters } from '@/store/slices/filtersSlice';
import { TaskStatus, TaskPriority, Task } from '@/types';
import { useFeaturePermission } from '@/shared/contexts/PermissionsContext';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';

import TaskListView from '../components/TaskListView';
import TaskKanbanView from '../components/TaskKanbanView';
import TaskDetailModal from '../components/TaskDetailModal';

/**
 * TasksPage
 * Main task management view with multiple view modes
 * Supports list, kanban, and calendar views
 * Mobile-first responsive design
 */
const TasksPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showError } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = useAppSelector(selectTaskViewMode);
  const filters = useAppSelector(selectTaskFilters);
  
  // Permission check
  const canCreateTask = useFeaturePermission('tasks', 'task_management', 'create');

  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [sortAnchor, setSortAnchor] = useState<null | HTMLElement>(null);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const kanbanPageSize = 500;

  /** RACI tab: 0=Responsible, 1=Accountable, 2=Consulted, 3=Informed */
  const [raciTab, setRaciTab] = useState(0);
  const raciRoleMap: Record<number, 'responsible' | 'accountable' | 'consulted' | 'informed'> = {
    0: 'responsible',
    1: 'accountable',
    2: 'consulted',
    3: 'informed',
  };
  const selectedRaciRole = raciRoleMap[raciTab];

  // Check if task detail should be shown
  const selectedTaskId = searchParams.get('taskId');

  // Build server filter: status list, projectId, raciRole, and search for my-tasks API
  const serverFilters = useMemo(
    () => ({
      status: filters.status,
      projectId: filters.projectId,
      raciRole: selectedRaciRole,
      search: searchQuery.trim() || undefined,
    }),
    [filters.status, filters.projectId, selectedRaciRole, searchQuery]
  );

  // Fetch tasks with pagination and server-side status/projectId/raciRole filter
  const effectivePageSize = viewMode === 'kanban' ? kanbanPageSize : pageSize;
  const effectivePage = viewMode === 'kanban' ? 1 : page;

  const { data, isLoading, refetch } = useQuery({
    queryKey: [...QUERY_KEYS.myTasks, effectivePage, effectivePageSize, serverFilters, viewMode],
    queryFn: () =>
      taskService.getMyTasks(effectivePage, effectivePageSize, {
        status: filters.status?.length ? filters.status : undefined,
        projectId: filters.projectId,
        raciRole: selectedRaciRole,
        search: searchQuery.trim() || undefined,
      }),
    staleTime: 30000,
    placeholderData: (prev: any) => prev,
    refetchOnWindowFocus: false,
  });

  const { data: validationData } = useQuery({
    queryKey: [...QUERY_KEYS.myTasks, 'validation', serverFilters],
    queryFn: () =>
      taskService.getMyTasks(1, kanbanPageSize, {
        projectId: filters.projectId,
        raciRole: selectedRaciRole,
      }),
    staleTime: 30000,
  });
  const validationTasks: Task[] = validationData?.items ?? [];

  const paginatedData = data as any;
  const tasks = paginatedData?.items ?? [];
  const total = paginatedData?.total ?? 0;
  const totalPages = paginatedData?.totalPages ?? 1;

  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) => taskService.deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myTasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
      handleCloseTaskDetail();
    },
    onError: (error) => {
      showError(error instanceof Error ? error.message : 'Failed to delete task');
    },
  });

  // Fetch incomplete counts for all RACI roles (exclude completed status)
  const incompleteStatuses = [TaskStatus.NOT_STARTED, TaskStatus.IN_PROGRESS, TaskStatus.BLOCKED];
  
  const { data: responsibleCountData } = useQuery({
    queryKey: [...QUERY_KEYS.myTasks, 'count', 'responsible', filters.projectId],
    queryFn: () => taskService.getMyTasks(1, 1, { 
      status: incompleteStatuses,
      projectId: filters.projectId, 
      raciRole: 'responsible' 
    }),
    staleTime: 30000,
  });

  const { data: accountableCountData } = useQuery({
    queryKey: [...QUERY_KEYS.myTasks, 'count', 'accountable', filters.projectId],
    queryFn: () => taskService.getMyTasks(1, 1, { 
      status: incompleteStatuses,
      projectId: filters.projectId, 
      raciRole: 'accountable' 
    }),
    staleTime: 30000,
  });

  const { data: consultedCountData } = useQuery({
    queryKey: [...QUERY_KEYS.myTasks, 'count', 'consulted', filters.projectId],
    queryFn: () => taskService.getMyTasks(1, 1, { 
      status: incompleteStatuses,
      projectId: filters.projectId, 
      raciRole: 'consulted' 
    }),
    staleTime: 30000,
  });

  const { data: informedCountData } = useQuery({
    queryKey: [...QUERY_KEYS.myTasks, 'count', 'informed', filters.projectId],
    queryFn: () => taskService.getMyTasks(1, 1, { 
      status: incompleteStatuses,
      projectId: filters.projectId, 
      raciRole: 'informed' 
    }),
    staleTime: 30000,
  });

  // Get incomplete counts from total field in pagination response
  const incompleteCounts = useMemo(() => {
    return {
      responsible: responsibleCountData?.total || 0,
      accountable: accountableCountData?.total || 0,
      consulted: consultedCountData?.total || 0,
      informed: informedCountData?.total || 0,
    };
  }, [responsibleCountData, accountableCountData, consultedCountData, informedCountData]);

  // Client-side priority and overdue filter (search is server-side)
  const filteredTasks = useMemo(() => {
    let result = [...tasks];

    if (filters.priority && filters.priority.length > 0) {
      result = result.filter((task) => filters.priority?.includes(task.priority));
    }

    if (filters.isOverdue) {
      const now = new Date();
      result = result.filter(
        (task) =>
          task.status !== TaskStatus.COMPLETED &&
          task.status !== TaskStatus.CANCELLED &&
          task.dueDate &&
          new Date(task.dueDate) < now
      );
    }

    return result;
  }, [tasks, filters.priority, filters.isOverdue]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Reset to page 1 when filters that affect server request change
  React.useEffect(() => {
    setPage(1);
  }, [filters.status, filters.projectId, selectedRaciRole, searchQuery]);

  // Count active filters
  const activeFilterCount = [
    filters.status?.length || 0,
    filters.priority?.length || 0,
    filters.isOverdue ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: 'list' | 'kanban' | 'calendar' | null
  ) => {
    if (newMode) {
      dispatch(setTaskViewMode(newMode));
    }
  };

  const handleCreateTask = () => {
    dispatch(openModal({ type: 'taskCreate' }));
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleCloseTaskDetail = () => {
    // If taskId param exists and we have navigation history, go back
    // Otherwise just clear the search params
    if (selectedTaskId && window.history.length > 1) {
      navigate(-1);
    } else {
      setSearchParams({});
    }
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleStatusFilter = (status: TaskStatus) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter((s) => s !== status)
      : [...currentStatuses, status];
    dispatch(updateTaskFilter({ status: newStatuses.length > 0 ? newStatuses : undefined }));
  };

  const handlePriorityFilter = (priority: TaskPriority) => {
    const currentPriorities = filters.priority || [];
    const newPriorities = currentPriorities.includes(priority)
      ? currentPriorities.filter((p) => p !== priority)
      : [...currentPriorities, priority];
    dispatch(updateTaskFilter({ priority: newPriorities.length > 0 ? newPriorities : undefined }));
  };

  const handleClearFilters = () => {
    dispatch(clearTaskFilters());
    handleFilterClose();
  };

  return (
    <Box>
      {/* Task count summary */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography variant="body2" color="text.secondary">
          {total > 0
            ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total} task${total !== 1 ? 's' : ''}`
            : 'No tasks'}
        </Typography>
      </Box>

      {/* RACI Role Tabs */}
      <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={raciTab}
          onChange={(_, v) => setRaciTab(v)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '0.85rem' },
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Responsible</span>
                {incompleteCounts.responsible > 0 && (
                  <Chip 
                    label={incompleteCounts.responsible} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      minWidth: 20,
                      fontSize: '0.7rem',
                      bgcolor: 'warning.main',
                      color: 'white',
                      fontWeight: 600
                    }} 
                  />
                )}
              </Box>
            }
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Accountable</span>
                {incompleteCounts.accountable > 0 && (
                  <Chip 
                    label={incompleteCounts.accountable} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      minWidth: 20,
                      fontSize: '0.7rem',
                      bgcolor: 'warning.main',
                      color: 'white',
                      fontWeight: 600
                    }} 
                  />
                )}
              </Box>
            }
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Consulted</span>
                {incompleteCounts.consulted > 0 && (
                  <Chip 
                    label={incompleteCounts.consulted} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      minWidth: 20,
                      fontSize: '0.7rem',
                      bgcolor: 'warning.main',
                      color: 'white',
                      fontWeight: 600
                    }} 
                  />
                )}
              </Box>
            }
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Informed</span>
                {incompleteCounts.informed > 0 && (
                  <Chip 
                    label={incompleteCounts.informed} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      minWidth: 20,
                      fontSize: '0.7rem',
                      bgcolor: 'warning.main',
                      color: 'white',
                      fontWeight: 600
                    }} 
                  />
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Mobile Toolbar */}
      {isMobile ? (
        <Box sx={{ mb: 2 }}>
          {/* Search Bar - Full width on mobile */}
          {mobileSearchOpen ? (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} style={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton onClick={() => { setMobileSearchOpen(false); setSearchQuery(''); }}>
                <X size={20} />
              </IconButton>
            </Box>
          ) : null}
          
          {/* Quick Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Search Toggle */}
            <IconButton
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              sx={{ 
                border: '1px solid',
                borderColor: mobileSearchOpen ? 'primary.main' : 'divider',
                borderRadius: 2,
                color: mobileSearchOpen ? 'primary.main' : 'text.secondary',
              }}
            >
              <Search size={20} />
            </IconButton>

            {/* Filter Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Filter size={16} />}
              onClick={() => setMobileFilterOpen(true)}
              sx={{ 
                borderColor: 'divider',
                minWidth: 'auto',
                px: 1.5,
              }}
            >
              {activeFilterCount > 0 && (
                <Chip
                  size="small"
                  label={activeFilterCount}
                  color="primary"
                  sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                />
              )}
            </Button>

            {/* View Mode Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{ ml: 'auto' }}
            >
              <ToggleButton value="list" sx={{ px: 1.5 }}>
                <List size={18} />
              </ToggleButton>
              <ToggleButton value="kanban" sx={{ px: 1.5 }}>
                <LayoutGrid size={18} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Mobile Filter Drawer */}
          <SwipeableDrawer
            anchor="bottom"
            open={mobileFilterOpen}
            onClose={() => setMobileFilterOpen(false)}
            onOpen={() => setMobileFilterOpen(true)}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                maxHeight: '80vh',
                pb: 'env(safe-area-inset-bottom)',
              },
            }}
          >
            <Box sx={{ p: 2 }}>
              {/* Handle bar */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box sx={{ width: 40, height: 4, bgcolor: 'grey.300', borderRadius: 2 }} />
              </Box>
              
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Filters
              </Typography>

              {/* Status Filters */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                STATUS
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {Object.values(TaskStatus).map((status) => (
                  <Chip
                    key={status}
                    label={status.replace('_', ' ')}
                    onClick={() => handleStatusFilter(status)}
                    color={filters.status?.includes(status) ? 'primary' : 'default'}
                    variant={filters.status?.includes(status) ? 'filled' : 'outlined'}
                    sx={{ textTransform: 'capitalize' }}
                  />
                ))}
              </Box>

              {/* Priority Filters */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                PRIORITY
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {Object.values(TaskPriority).map((priority) => (
                  <Chip
                    key={priority}
                    label={priority.toUpperCase()}
                    onClick={() => handlePriorityFilter(priority)}
                    color={filters.priority?.includes(priority) ? 'primary' : 'default'}
                    variant={filters.priority?.includes(priority) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => { handleClearFilters(); setMobileFilterOpen(false); }}
                  disabled={activeFilterCount === 0}
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setMobileFilterOpen(false)}
                >
                  Apply Filters
                </Button>
              </Box>
            </Box>
          </SwipeableDrawer>
        </Box>
      ) : (
        /* Desktop Toolbar */
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            flexWrap: 'wrap',
          }}
        >
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} style={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          {/* Filters */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Filter size={16} />}
            endIcon={
              activeFilterCount > 0 ? (
                <Chip
                  size="small"
                  label={activeFilterCount}
                  color="primary"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              ) : (
                <ChevronDown size={14} />
              )
            }
            onClick={handleFilterClick}
            sx={{ borderColor: 'divider' }}
          >
            Filters
          </Button>

          {/* Filter Menu */}
          <Menu
            anchorEl={filterAnchor}
            open={Boolean(filterAnchor)}
            onClose={handleFilterClose}
            PaperProps={{ sx: { minWidth: 240, p: 1 } }}
          >
            <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
              STATUS
            </Typography>
            {Object.values(TaskStatus).map((status) => (
              <MenuItem
                key={status}
                onClick={() => handleStatusFilter(status)}
                selected={filters.status?.includes(status)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                {status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </MenuItem>
            ))}
            
            <Divider sx={{ my: 1 }} />
            
            <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
              PRIORITY
            </Typography>
            {Object.values(TaskPriority).map((priority) => (
              <MenuItem
                key={priority}
                onClick={() => handlePriorityFilter(priority)}
                selected={filters.priority?.includes(priority)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                {priority.toUpperCase()}
              </MenuItem>
            ))}

            {activeFilterCount > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <MenuItem onClick={handleClearFilters} sx={{ color: 'error.main' }}>
                  Clear All Filters
                </MenuItem>
              </>
            )}
          </Menu>

          <Box sx={{ flex: 1 }} />

          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="list">
              <List size={18} />
            </ToggleButton>
            <ToggleButton value="kanban">
              <LayoutGrid size={18} />
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Create Task Button */}
          {canCreateTask && (
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={handleCreateTask}
            >
              New Task
            </Button>
          )}
        </Box>
      )}

      {/* Task Views */}
      {viewMode === 'list' && (
        <Box>
          <TaskListView
            tasks={filteredTasks}
            isLoading={isLoading}
            currentRaciRole={selectedRaciRole}
            allTasksForValidation={validationTasks}
          />
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      )}
      {viewMode === 'kanban' && (
        <Box>
          <TaskKanbanView tasks={filteredTasks} isLoading={isLoading} />
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      )}

      {/* Mobile FAB for Create Task */}
      {isMobile && canCreateTask && (
        <Fab
          color="primary"
          onClick={handleCreateTask}
          sx={{
            position: 'fixed',
            bottom: 80, // Above bottom nav
            right: 16,
            zIndex: 1100,
          }}
        >
          <Plus size={24} />
        </Fab>
      )}

      {/* Task Detail Modal */}
      <TaskDetailModal
        open={!!selectedTaskId}
        taskId={selectedTaskId}
        onClose={handleCloseTaskDetail}
        onEdit={(task: Task) => {
          // Allow editing tasks that are not completed or cancelled
          if (task.status !== TaskStatus.COMPLETED && task.status !== TaskStatus.CANCELLED) {
            handleCloseTaskDetail();
            dispatch(openModal({ type: 'taskEdit', data: task }));
          }
        }}
        onDelete={(taskId: string) => {
          deleteTaskMutation.mutate(taskId);
        }}
      />
    </Box>
  );
};

export default TasksPage;
